import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, UploadCloud, ImageOff, ArrowUp, ArrowDown, Type, FileText, Palette, LayoutList } from "lucide-react";
import { API, fileSrc, invalidateSettingsCache } from "@/lib/api";

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors text-sm";

const SUBTABS = [
  { id: "content", label: "კონტენტი", icon: Type },
  { id: "sections", label: "სექციები", icon: LayoutList },
  { id: "legal", label: "სამართლებრივი", icon: FileText },
  { id: "seo", label: "SEO", icon: FileText },
  { id: "design", label: "დიზაინი", icon: Palette },
];

const SECTION_LABELS = {
  intro: "კომპანიის შესავალი",
  benefits: "უპირატესობები",
  featured: "თავისუფალი ავტომობილები",
  fleet: "კატეგორიების პრევიუ",
  blog: "ბლოგის ბოლო სტატიები",
  cta: "საბოლოო CTA ბლოკი",
};

const SEO_PAGES = [
  { id: "home", label: "მთავარი" },
  { id: "catalog", label: "კატალოგი" },
  { id: "apply", label: "განაცხადი" },
  { id: "about", label: "ჩვენ შესახებ" },
  { id: "privacy", label: "კონფიდენციალურობა" },
  { id: "terms", label: "წესები და პირობები" },
  { id: "manqanis-gakiraveba-taxisistvis", label: "მანქანის ქირაობა ტაქსისთვის" },
  { id: "prius-gakiraveba", label: "Prius ქირაობა" },
  { id: "tesla-gakiraveba", label: "Tesla ქირაობა" },
  { id: "elektro-manqanebi-taxisistvis", label: "ელექტრო მანქანები" },
  { id: "tavisufali-manqanebi", label: "თავისუფალი ავტომობილები" },
];

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function AdminSiteSettings({ auth }) {
  const [form, setForm] = useState(null);
  const [sub, setSub] = useState("content");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const heroInput = useRef(null);
  const ogInput = useRef(null);

  const load = () => {
    axios.get(`${API}/settings`).then((r) => setForm(r.data)).catch(() => {});
  };

  useEffect(load, []);

  if (!form) return <p className="text-gray-500">იტვირთება...</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setDesign = (k) => (e) => setForm({ ...form, design: { ...form.design, [k]: e.target.value } });
  const setSeo = (page, k) => (e) =>
    setForm({ ...form, seo: { ...form.seo, [page]: { ...(form.seo?.[page] || {}), [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value } } });
  const setBenefit = (i, k) => (e) => {
    const benefits = form.benefits.map((b, j) => (j === i ? { ...b, [k]: e.target.value } : b));
    setForm({ ...form, benefits });
  };

  const save = async (keys) => {
    setSaving(true);
    try {
      const payload = {};
      keys.forEach((k) => (payload[k] = form[k]));
      await axios.put(`${API}/admin/settings/site`, payload, auth);
      invalidateSettingsCache();
      toast.success("შენახულია — საიტი უკვე განახლებულია");
    } catch {
      toast.error("შენახვა ვერ მოხერხდა");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (endpoint, file, key) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(key);
    try {
      const { data } = await axios.post(`${API}/admin/settings/${endpoint}`, fd, auth);
      setForm({ ...form, ...data });
      invalidateSettingsCache();
      toast.success("სურათი განახლდა");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "ატვირთვა ვერ მოხერხდა");
    } finally {
      setUploading(null);
    }
  };

  const moveSection = (i, dir) => {
    const arr = [...form.sections];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setForm({ ...form, sections: arr });
  };

  const toggleSection = (i) => {
    const arr = form.sections.map((s, j) => (j === i ? { ...s, enabled: !s.enabled } : s));
    setForm({ ...form, sections: arr });
  };

  const SaveBtn = ({ keys }) => (
    <button onClick={() => save(keys)} disabled={saving} className="btn-gold !py-2.5 !px-6 text-sm disabled:opacity-60" data-testid={`settings-save-${keys[0]}`}>
      <Save className="w-4 h-4" /> {saving ? "ინახება..." : "შენახვა"}
    </button>
  );

  const hero = form.hero_image_url ? fileSrc(form.hero_image_url) : null;
  const og = form.og_image_url ? fileSrc(form.og_image_url) : null;
  const d = form.design || {};

  return (
    <div data-testid="admin-site-settings">
      <div className="flex flex-wrap gap-2 mb-8" data-testid="settings-subtabs">
        {SUBTABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            data-testid={`settings-subtab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition-colors ${
              sub === t.id ? "bg-[#D4AF37] text-[#0A0B0E] border-[#D4AF37]" : "bg-white/5 text-gray-300 border-white/15 hover:border-white/40"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {sub === "content" && (
        <div className="space-y-8">
          <section className="card-lux p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">ჰერო</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <div className="h-36 rounded-xl overflow-hidden bg-[#0D0F14] mb-3">
                  {hero ? <img src={hero} alt="" className="w-full h-full object-cover" /> : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-gray-600">
                      <ImageOff className="w-7 h-7" /><span className="text-xs">ფონის ფოტო არ არის</span>
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => heroInput.current?.click()} disabled={uploading === "hero"} data-testid="hero-image-upload-btn" className="btn-ghost !py-2 !px-4 text-xs w-full">
                  <UploadCloud className="w-3.5 h-3.5" /> {uploading === "hero" ? "იტვირთება..." : "ფოტოს შეცვლა"}
                </button>
                <input type="file" accept="image/*" hidden ref={heroInput} onChange={(e) => uploadImage("hero-image", e.target.files[0], "hero")} />
              </div>
              <div className="lg:col-span-2 grid gap-4">
                <Field label="მთავარი სათაური"><input data-testid="settings-hero-headline" value={form.hero_headline} onChange={set("hero_headline")} className={inputCls} /></Field>
                <Field label="აღწერა"><textarea rows={3} data-testid="settings-hero-description" value={form.hero_description} onChange={set("hero_description")} className={`${inputCls} resize-none`} /></Field>
                <Field label="ღილაკის ტექსტი"><input data-testid="settings-hero-cta" value={form.hero_cta} onChange={set("hero_cta")} className={inputCls} /></Field>
              </div>
            </div>
            <SaveBtn keys={["hero_headline", "hero_description", "hero_cta"]} />
          </section>

          <section className="card-lux p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">შესავალი და კატალოგი</h2>
            <div className="grid gap-4">
              <Field label="შესავლის სათაური"><input data-testid="settings-intro-title" value={form.intro_title} onChange={set("intro_title")} className={inputCls} /></Field>
              <Field label="კომპანიის აღწერა"><textarea rows={3} data-testid="settings-intro" value={form.intro} onChange={set("intro")} className={`${inputCls} resize-none`} /></Field>
              <Field label="კატალოგის შესავალი ტექსტი"><textarea rows={2} data-testid="settings-catalog-intro" value={form.catalog_intro} onChange={set("catalog_intro")} className={`${inputCls} resize-none`} /></Field>
              <Field label="ფუტერის ტექსტი"><textarea rows={2} data-testid="settings-footer-text" value={form.footer_text} onChange={set("footer_text")} className={`${inputCls} resize-none`} /></Field>
            </div>
            <SaveBtn keys={["intro_title", "intro", "catalog_intro", "footer_text"]} />
          </section>

          <section className="card-lux p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">უპირატესობები (4 ბლოკი)</h2>
            <Field label="სექციის სათაური"><input value={form.benefits_title} onChange={set("benefits_title")} className={inputCls} /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {form.benefits.map((b, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3" data-testid={`settings-benefit-${i}`}>
                  <input value={b.title} onChange={setBenefit(i, "title")} className={inputCls} placeholder={`უპირატესობა ${i + 1} — სათაური`} />
                  <textarea rows={2} value={b.text} onChange={setBenefit(i, "text")} className={`${inputCls} resize-none`} placeholder="ტექსტი" />
                </div>
              ))}
            </div>
            <SaveBtn keys={["benefits", "benefits_title"]} />
          </section>

          <section className="card-lux p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">სექციების სათაურები</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="თავისუფალი ავტომობილები"><input value={form.featured_title} onChange={set("featured_title")} className={inputCls} /></Field>
              <Field label="კატეგორიების სათაური"><input value={form.fleet_title} onChange={set("fleet_title")} className={inputCls} /></Field>
              <Field label="კატეგორიების ზე-სათაური"><input value={form.fleet_eyebrow} onChange={set("fleet_eyebrow")} className={inputCls} /></Field>
              <Field label="CTA სათაური"><input value={form.cta_title} onChange={set("cta_title")} className={inputCls} /></Field>
              <Field label="CTA ტექსტი"><input value={form.cta_text} onChange={set("cta_text")} className={inputCls} /></Field>
              <Field label="CTA ღილაკი"><input value={form.cta_button} onChange={set("cta_button")} className={inputCls} /></Field>
            </div>
            <SaveBtn keys={["featured_title", "fleet_title", "fleet_eyebrow", "cta_title", "cta_text", "cta_button"]} />
          </section>

          <section className="card-lux p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">სარეკლამო ბლოკი და "მალე დაემატება"</h2>
            <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" data-testid="settings-promo-enabled" checked={!!form.promo_enabled} onChange={set("promo_enabled")} className="w-4 h-4 accent-[#D4AF37]" />
              სარეკლამო ბლოკის ჩვენება მთავარ გვერდზე
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="სარეკლამო სათაური"><input value={form.promo_title} onChange={set("promo_title")} className={inputCls} /></Field>
              <Field label="სარეკლამო ტექსტი"><input value={form.promo_text} onChange={set("promo_text")} className={inputCls} /></Field>
            </div>
            <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" data-testid="settings-soon-enabled" checked={!!form.soon_enabled} onChange={set("soon_enabled")} className="w-4 h-4 accent-[#D4AF37]" />
              "მალე დაემატება" ბარათი კატალოგში
            </label>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="ბეიჯი"><input value={form.soon_badge} onChange={set("soon_badge")} className={inputCls} /></Field>
              <Field label="სათაური"><input value={form.soon_title} onChange={set("soon_title")} className={inputCls} /></Field>
              <Field label="ტექსტი"><input value={form.soon_text} onChange={set("soon_text")} className={inputCls} /></Field>
            </div>
            <SaveBtn keys={["promo_enabled", "promo_title", "promo_text", "soon_enabled", "soon_badge", "soon_title", "soon_text"]} />
          </section>

          <section className="card-lux p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">ნავიგაცია და კონტაქტი</h2>
            <div className="grid sm:grid-cols-4 gap-4">
              <Field label="მენიუ: მთავარი"><input value={form.nav_home} onChange={set("nav_home")} className={inputCls} /></Field>
              <Field label="მენიუ: კატალოგი"><input value={form.nav_catalog} onChange={set("nav_catalog")} className={inputCls} /></Field>
              <Field label="მენიუ: ჩვენ შესახებ"><input value={form.nav_about} onChange={set("nav_about")} className={inputCls} /></Field>
              <Field label="მენიუ: განაცხადი"><input value={form.nav_apply} onChange={set("nav_apply")} className={inputCls} /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="ტელეფონი"><input data-testid="settings-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
              <Field label="ელფოსტა"><input data-testid="settings-email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
              <Field label="მისამართი"><input data-testid="settings-address" value={form.address} onChange={set("address")} className={inputCls} /></Field>
              <Field label="Google Maps ბმული"><input data-testid="settings-maps-url" value={form.maps_url || ""} onChange={set("maps_url")} className={inputCls} placeholder="https://share.google/..." /></Field>
              <Field label="WhatsApp ბმული"><input data-testid="settings-whatsapp-url" value={form.whatsapp_url || ""} onChange={set("whatsapp_url")} className={inputCls} placeholder="https://wa.me/995XXXXXXXXX" /></Field>
              <Field label="Facebook"><input data-testid="settings-facebook" value={form.facebook} onChange={set("facebook")} className={inputCls} placeholder="https://facebook.com/..." /></Field>
              <Field label="Instagram"><input value={form.instagram} onChange={set("instagram")} className={inputCls} placeholder="https://instagram.com/..." /></Field>
            </div>
            <SaveBtn keys={["nav_home", "nav_catalog", "nav_about", "nav_apply", "phone", "email", "address", "maps_url", "facebook", "instagram"]} />
          </section>
        </div>
      )}

      {sub === "sections" && (
        <section className="card-lux p-6 space-y-4" data-testid="settings-sections">
          <h2 className="text-lg font-semibold text-white">მთავარი გვერდის სექციები — რიგი და ხილვადობა</h2>
          <div className="space-y-3">
            {form.sections.map((s, i) => (
              <div key={s.id} className="flex items-center justify-between gap-3 p-4 rounded-xl bg-white/5 border border-white/10" data-testid={`section-row-${s.id}`}>
                <label className="flex items-center gap-3 text-sm text-gray-200 cursor-pointer">
                  <input type="checkbox" checked={!!s.enabled} onChange={() => toggleSection(i)} data-testid={`section-toggle-${s.id}`} className="w-4 h-4 accent-[#D4AF37]" />
                  {SECTION_LABELS[s.id] || s.id}
                </label>
                <div className="flex gap-2">
                  <button onClick={() => moveSection(i, -1)} disabled={i === 0} data-testid={`section-up-${s.id}`} className="w-8 h-8 rounded-lg bg-white/10 text-white flex items-center justify-center disabled:opacity-30">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button onClick={() => moveSection(i, 1)} disabled={i === form.sections.length - 1} data-testid={`section-down-${s.id}`} className="w-8 h-8 rounded-lg bg-white/10 text-white flex items-center justify-center disabled:opacity-30">
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <SaveBtn keys={["sections"]} />
        </section>
      )}

      {sub === "legal" && (
        <section className="card-lux p-6 space-y-4" data-testid="settings-legal">
          <h2 className="text-lg font-semibold text-white">სამართლებრივი ტექსტები</h2>
          <p className="text-xs text-gray-500">ცარიელი ველის შემთხვევაში გამოჩნდება ნაგულისხმევი ტექსტი. პარაგრაფები გაყავი ცარიელი ხაზით.</p>
          <Field label="წესები და პირობები (სრული ტექსტი)">
            <textarea rows={10} data-testid="settings-terms-text" value={form.terms_text} onChange={set("terms_text")} className={`${inputCls} resize-y font-mono text-xs`} placeholder="დატოვე ცარიელი ნაგულისხმევი ტექსტისთვის" />
          </Field>
          <Field label="კონფიდენციალურობის პოლიტიკა (სრული ტექსტი)">
            <textarea rows={10} data-testid="settings-privacy-text" value={form.privacy_text} onChange={set("privacy_text")} className={`${inputCls} resize-y font-mono text-xs`} placeholder="დატოვე ცარიელი ნაგულისხმევი ტექსტისთვის" />
          </Field>
          <SaveBtn keys={["terms_text", "privacy_text"]} />
        </section>
      )}

      {sub === "seo" && (
        <div className="space-y-8" data-testid="settings-seo">
          <section className="card-lux p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">გვერდების SEO</h2>
            {SEO_PAGES.map((p) => {
              const ov = form.seo?.[p.id] || {};
              return (
                <div key={p.id} className="p-4 rounded-xl bg-white/5 border border-white/10 grid sm:grid-cols-2 gap-4" data-testid={`seo-page-${p.id}`}>
                  <p className="sm:col-span-2 text-sm font-semibold text-white">{p.label}</p>
                  <Field label="SEO სათაური"><input value={ov.title || ""} onChange={setSeo(p.id, "title")} className={inputCls} /></Field>
                  <Field label="Meta აღწერა"><input value={ov.description || ""} onChange={setSeo(p.id, "description")} className={inputCls} /></Field>
                  <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
                    <input type="checkbox" checked={!!ov.noindex} onChange={setSeo(p.id, "noindex")} className="w-4 h-4 accent-[#D4AF37]" />
                    არ დაინდექსირდეს Google-ში (noindex)
                  </label>
                </div>
              );
            })}
            <SaveBtn keys={["seo"]} />
          </section>
          <section className="card-lux p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">სოციალური გაზიარება (Open Graph)</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="OG სათაური"><input value={form.og_title} onChange={set("og_title")} className={inputCls} /></Field>
              <Field label="OG აღწერა"><input value={form.og_description} onChange={set("og_description")} className={inputCls} /></Field>
            </div>
            <div className="flex items-center gap-4">
              <div className="h-24 w-40 rounded-xl overflow-hidden bg-[#0D0F14]">
                {og ? <img src={og} alt="" className="w-full h-full object-cover" /> : (
                  <div className="w-full h-full flex items-center justify-center text-gray-600"><ImageOff className="w-6 h-6" /></div>
                )}
              </div>
              <button type="button" onClick={() => ogInput.current?.click()} disabled={uploading === "og"} data-testid="og-image-upload-btn" className="btn-ghost !py-2 !px-4 text-xs">
                <UploadCloud className="w-3.5 h-3.5" /> {uploading === "og" ? "იტვირთება..." : "სურათის ატვირთვა"}
              </button>
              <input type="file" accept="image/*" hidden ref={ogInput} onChange={(e) => uploadImage("og-image", e.target.files[0], "og")} />
            </div>
            <SaveBtn keys={["og_title", "og_description"]} />
          </section>
          <section className="card-lux p-6 space-y-4" data-testid="settings-integrations">
            <h2 className="text-lg font-semibold text-white">Google და Meta ინტეგრაციები</h2>
            <p className="text-xs text-gray-500">ჩაწერე ID/კოდი და საიტი ავტომატურად დააკავშირდება — კოდის ჩახვრიტვა არ არის საჭირო.</p>
            <div className="grid sm:grid-cols-1 gap-4">
              <Field label="Google Search Console — verification კოდი"><input data-testid="settings-gsc" value={form.google_site_verification || ""} onChange={set("google_site_verification")} className={inputCls} placeholder="მაგ. abCdEf123..." /></Field>
              <Field label="Google Analytics 4 — Measurement ID"><input data-testid="settings-ga" value={form.ga_measurement_id || ""} onChange={set("ga_measurement_id")} className={inputCls} placeholder="G-XXXXXXXXXX" /></Field>
              <Field label="Meta Pixel ID"><input data-testid="settings-meta-pixel" value={form.meta_pixel_id || ""} onChange={set("meta_pixel_id")} className={inputCls} placeholder="123456789012345" /></Field>
            </div>
            <SaveBtn keys={["google_site_verification", "ga_measurement_id", "meta_pixel_id"]} />
          </section>
        </div>
      )}

      {sub === "design" && (
        <section className="card-lux p-6 space-y-6" data-testid="settings-design">
          <h2 className="text-lg font-semibold text-white">დიზაინის პარამეტრები</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {[
              ["accent", "მთავარი (გოლი)"],
              ["accent_soft", "აქცენტი (ღია გოლი)"],
              ["background", "ფონი"],
              ["card", "ბარათის ფონი"],
              ["text", "ტექსტის ფერი"],
              ["border", "ჩარჩოს ფერი"],
              ["button", "ღილაკის ფერი"],
            ].map(([k, label]) => (
              <div key={k} className="space-y-2" data-testid={`design-color-${k}`}>
                <label className="block text-xs text-gray-400">{label}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={d[k] || "#000000"} onChange={setDesign(k)} className="w-10 h-10 rounded-lg bg-transparent border border-white/15 cursor-pointer" />
                  <input value={d[k] || ""} onChange={setDesign(k)} className={`${inputCls} !py-2 text-xs font-mono`} />
                </div>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="გარეგნობა">
              <select data-testid="design-appearance" value={d.appearance} onChange={setDesign("appearance")} className={inputCls}>
                <option value="dark" className="bg-[#141721]">მუქი (ნაგულისხმევი)</option>
                <option value="soft" className="bg-[#141721]">ნაზი მუქი</option>
                <option value="graphite" className="bg-[#141721]">გრაფიტი</option>
              </select>
            </Field>
            <Field label="შრიფტის ზომა">
              <select data-testid="design-font" value={d.font_preset} onChange={setDesign("font_preset")} className={inputCls}>
                <option value="small" className="bg-[#141721]">პატარა</option>
                <option value="medium" className="bg-[#141721]">საშუალო</option>
                <option value="large" className="bg-[#141721]">დიდი</option>
              </select>
            </Field>
            <Field label="სათაურების ზომა">
              <select data-testid="design-heading" value={d.heading_preset} onChange={setDesign("heading_preset")} className={inputCls}>
                <option value="small" className="bg-[#141721]">კომპაქტური</option>
                <option value="medium" className="bg-[#141721]">სტანდარტული</option>
                <option value="large" className="bg-[#141721]">გამოკვეთილი</option>
              </select>
            </Field>
            <Field label="ღილაკების ზომა">
              <select data-testid="design-button" value={d.button_size} onChange={setDesign("button_size")} className={inputCls}>
                <option value="small" className="bg-[#141721]">პატარა</option>
                <option value="medium" className="bg-[#141721]">საშუალო</option>
                <option value="large" className="bg-[#141721]">დიდი</option>
              </select>
            </Field>
            <Field label="სექციების დაშორება">
              <select data-testid="design-spacing" value={d.section_spacing} onChange={setDesign("section_spacing")} className={inputCls}>
                <option value="compact" className="bg-[#141721]">კომპაქტური</option>
                <option value="medium" className="bg-[#141721]">სტანდარტული</option>
                <option value="large" className="bg-[#141721]">ჰაეროვანი</option>
              </select>
            </Field>
            <Field label="კუთხეების სიმრგვალე">
              <select data-testid="design-radius" value={d.radius} onChange={setDesign("radius")} className={inputCls}>
                <option value="0.375rem" className="bg-[#141721]">მკვეთრი</option>
                <option value="0.75rem" className="bg-[#141721]">სტანდარტული</option>
                <option value="1.25rem" className="bg-[#141721]">მრგვალი</option>
              </select>
            </Field>
          </div>
          <SaveBtn keys={["design"]} />
        </section>
      )}
    </div>
  );
}
